// WooCommerce API Service V2 - Alternative approach using newer API endpoints
// This service handles all API calls to the headless WooCommerce WordPress site

import OAuth from 'oauth-1.0a';
import CryptoJS from 'crypto-js';

const WOOCOMMERCE_API_BASE = process.env.REACT_APP_WOOCOMMERCE_API_URL || 'http://localhost:8888/Website/duchessCollection/wp-json/wc/v3';

const CONSUMER_KEY = process.env.REACT_APP_WOOCOMMERCE_CONSUMER_KEY || '';
const CONSUMER_SECRET = process.env.REACT_APP_WOOCOMMERCE_CONSUMER_SECRET || '';

// OAuth1 configuration for HTTP connections
const oauth = OAuth({
  consumer: {
    key: CONSUMER_KEY,
    secret: CONSUMER_SECRET
  },
  signature_method: 'HMAC-SHA1',
  hash_function(base_string, key) {
    return CryptoJS.HmacSHA1(base_string, key).toString(CryptoJS.enc.Base64);
  }
});

// Debug: Log the credentials being used

// Helper function to create OAuth1 headers for HTTP connections
const createOAuthHeaders = (url, method = 'GET') => {
  const requestData = {
    url: url,
    method: method
  };
  
  return oauth.toHeader(oauth.authorize(requestData));
};

// Helper function to create basic auth header (for HTTPS connections)
const createAuthHeader = () => {
  const credentials = btoa(`${CONSUMER_KEY}:${CONSUMER_SECRET}`);
  return `Basic ${credentials}`;
};

// Helper function to build query parameters
const buildQueryParams = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value);
    }
  });
  return searchParams.toString();
};

// Alternative authentication method using query parameters
const createQueryAuth = () => {
  return `consumer_key=${CONSUMER_KEY}&consumer_secret=${CONSUMER_SECRET}`;
};

export const woocommerceServiceV2 = {
  // Fetch all products with pagination and filtering
  async getProducts(options = {}) {
    try {
      const {
        page = 1,
        per_page = 12,
        category = '',
        search = '',
        status = 'publish',
        featured = false,
        on_sale = false,
        orderby = 'date',
        order = 'desc'
      } = options;

      const params = {
        page,
        per_page,
        status,
        orderby,
        order
      };

      // Add optional filters
      if (category) params.category = category;
      if (search) params.search = search;
      if (featured) params.featured = true;
      if (on_sale) params.on_sale = true;

      // Build URL without authentication parameters
      const queryString = buildQueryParams(params);
      const url = `${WOOCOMMERCE_API_BASE}/products?${queryString}`;

      // Use OAuth1 for HTTP connections
      const oauthHeaders = createOAuthHeaders(url, 'GET');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...oauthHeaders
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        throw new Error(`Failed to fetch products: ${response.status} ${response.statusText} - ${errorData.message || 'Unknown error'}`);
      }

      const products = await response.json();

      return products;
    } catch (error) {

      throw error;
    }
  },

  // Fetch a single product by ID
  async getProduct(productId) {
    try {
      const url = `${WOOCOMMERCE_API_BASE}/products/${productId}`;
      
      // Use OAuth1 for HTTP connections
      const oauthHeaders = createOAuthHeaders(url, 'GET');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...oauthHeaders
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${response.status} ${response.statusText}`);
      }

      const product = await response.json();
      return product;
    } catch (error) {

      throw error;
    }
  },

  // Fetch product categories
  async getCategories(options = {}) {
    try {
      const {
        page = 1,
        per_page = 100,
        parent = 0,
        orderby = 'name',
        order = 'asc'
      } = options;

      const params = {
        page,
        per_page,
        parent,
        orderby,
        order
      };

      const queryString = buildQueryParams(params);
      const url = `${WOOCOMMERCE_API_BASE}/products/categories?${queryString}`;
      
      // Use OAuth1 for HTTP connections
      const oauthHeaders = createOAuthHeaders(url, 'GET');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...oauthHeaders
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch categories: ${response.status} ${response.statusText}`);
      }

      const categories = await response.json();
      return categories;
    } catch (error) {

      throw error;
    }
  },

  // Search products
  async searchProducts(searchTerm, options = {}) {
    try {
      const searchOptions = {
        ...options,
        search: searchTerm
      };
      return await this.getProducts(searchOptions);
    } catch (error) {

      throw error;
    }
  },

  // Fetch featured products
  async getFeaturedProducts(options = {}) {
    try {
      const featuredOptions = {
        ...options,
        featured: true
      };
      return await this.getProducts(featuredOptions);
    } catch (error) {

      throw error;
    }
  },

  // Fetch products on sale
  async getSaleProducts(options = {}) {
    try {
      const saleOptions = {
        ...options,
        on_sale: true
      };
      return await this.getProducts(saleOptions);
    } catch (error) {

      throw error;
    }
  },

  // Fetch products by category
  async getProductsByCategory(categoryId, options = {}) {
    try {
      const categoryOptions = {
        ...options,
        category: categoryId
      };
      return await this.getProducts(categoryOptions);
    } catch (error) {

      throw error;
    }
  },

  // Get product variations (for variable products)
  async getProductVariations(productId) {
    try {
      const url = `${WOOCOMMERCE_API_BASE}/products/${productId}/variations`;
      
      // Use OAuth1 for HTTP connections
      const oauthHeaders = createOAuthHeaders(url, 'GET');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...oauthHeaders
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product variations: ${response.status} ${response.statusText}`);
      }

      const variations = await response.json();
      return variations;
    } catch (error) {

      throw error;
    }
  },

  // Get product reviews
  async getProductReviews(productId, options = {}) {
    try {
      const {
        page = 1,
        per_page = 10,
        status = 'approved'
      } = options;

      const params = {
        product: productId,
        page,
        per_page,
        status
      };

      const queryString = buildQueryParams(params);
      const url = `${WOOCOMMERCE_API_BASE}/products/reviews?${queryString}`;
      
      // Use OAuth1 for HTTP connections
      const oauthHeaders = createOAuthHeaders(url, 'GET');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...oauthHeaders
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product reviews: ${response.status} ${response.statusText}`);
      }

      const reviews = await response.json();
      return reviews;
    } catch (error) {

      throw error;
    }
  },

  // Test API connection
  async testConnection() {
    try {
      const url = `${WOOCOMMERCE_API_BASE}/system_status`;
      
      // Use OAuth1 for HTTP connections
      const oauthHeaders = createOAuthHeaders(url, 'GET');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...oauthHeaders
        },
      });

      if (!response.ok) {
        throw new Error(`API connection failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data
      };
    } catch (error) {

      return {
        success: false,
        error: error.message
      };
    }
  }
};

export default woocommerceServiceV2;